import React, { useEffect, useState, useContext } from 'react'
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import publicApi from '../services/public.api';
import LocationContext from '../context/LocationContext';
import { useCart } from '../context/CartContext';
import { addToCartAnimated } from '../utils/addToCartAnimated';
import ProductCardQuantity, { getDefaultCardSelection, isProductHasVariants } from './ProductCardQuantity';
import ProductCardPriceQtyRow from './ProductCardPriceQtyRow';
import { buildCartProductFromSelection } from '../utils/cartProductHelpers';

const NewArrivalOne = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addedProductId, setAddedProductId] = useState(null);
    const [selections, setSelections] = useState({});
    const [weightHintId, setWeightHintId] = useState(null);

    // Cart context
    const { addToCart } = useCart();

    // Location-based pricing
    const context = useContext(LocationContext);
    const isCasaRegion = context?.isCasaRegion ?? false;

    // Get price based on location
    const getLocationPrice = (product) => {
        if (!product) return 0;
        const priceCasa = parseFloat(product.price_casa || 0);
        const regularPrice = parseFloat(product.price || 0);
        return (isCasaRegion && priceCasa > 0) ? priceCasa : regularPrice;
    };

    const getSelection = (product) =>
        selections[product.id] || getDefaultCardSelection(isProductHasVariants(product));

    const updateSelection = (product, next) => {
        setSelections((prev) => ({ ...prev, [product.id]: next }));
    };

    const handleAddToCart = (e, product) => {
        e.preventDefault();
        e.stopPropagation();
        const built = buildCartProductFromSelection(
            product,
            getLocationPrice(product),
            getSelection(product)
        );
        if (built.error) {
            setWeightHintId(product.id);
            setTimeout(() => setWeightHintId(null), 2000);
            return;
        }
        addToCartAnimated({
            event: e,
            product: built.product,
            addToCart,
            quantity: built.quantity,
        });
        setAddedProductId(product.id);
        setTimeout(() => setAddedProductId(null), 1500);
    };

    function SampleNextArrow(props) {
        const { className, onClick } = props;
        return (
            <button
                type="button" onClick={onClick}
                className={` ${className} slick-next slick-arrow flex-center rounded-circle border border-gray-100 hover-border-main-600 text-xl hover-bg-main-600 hover-text-white transition-1`}
            >
                <i className="ph ph-caret-right" />
            </button>
        );
    }
    function SamplePrevArrow(props) {
        const { className, onClick } = props;

        return (
            <button
                type="button"
                onClick={onClick}
                className={`${className} slick-prev slick-arrow flex-center rounded-circle border border-gray-100 hover-border-main-600 text-xl hover-bg-main-600 hover-text-white transition-1`}
            >
                <i className="ph ph-caret-left" />
            </button>
        );
    }

    const settings = {
        dots: false,
        arrows: true,
        infinite: true,
        speed: 1000,
        slidesToShow: 6,
        slidesToScroll: 1,
        initialSlide: 0,
        autoplay: true,
        nextArrow: <SampleNextArrow />,
        prevArrow: <SamplePrevArrow />,
        responsive: [
            {
                breakpoint: 1599,
                settings: {
                    slidesToShow: 6,
                },
            },
            {
                breakpoint: 1399,
                settings: {
                    slidesToShow: 4,
                },
            },
            {
                breakpoint: 992,
                settings: {
                    slidesToShow: 3,
                },
            },
            {
                breakpoint: 575,
                settings: {
                    slidesToShow: 2,
                },
            },
            {
                breakpoint: 424,
                settings: {
                    slidesToShow: 1,
                },
            },
        ],
    };

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await publicApi.getAllProducts();
                if (response.success && response.data) {
                    setProducts(response.data);
                }
            } catch (error) {
                console.error('Error fetching all products:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, []);

    // Get image URL
    const getImageUrl = (imagePath) => {
        return publicApi.getImageUrl(imagePath);
    };

    // Format price
    const formatPrice = (price) => {
        return parseFloat(price || 0).toFixed(2);
    };

    // Don't render if no products
    if (!loading && products.length === 0) {
        return null;
    }

    return (
        <section className="new-arrival pb-80">
            <div className="container container-lg">
                <div className="section-heading">
                    <div className="flex-between flex-wrap gap-8">
                        <h5 className="mb-0">Nouveaux Produits</h5>
                        <div className="flex-align mr-point gap-16">
                            <Link
                                to="/shop"
                                className="text-sm fw-medium text-gray-700 hover-text-main-600 hover-text-decoration-underline"
                            >
                                Voir toutes les offres
                            </Link>
                        </div>
                    </div>
                </div>
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-main" role="status">
                            <span className="visually-hidden">Chargement...</span>
                        </div>
                    </div>
                ) : (
                    <div className="new-arrival__slider arrow-style-two">
                        <Slider {...settings}>
                            {products.map((product) => (
                                <div key={product.id} className="new-arrival-slide">
                                    <div className="product-card new-arrival-product-hover px-8 py-16 border border-gray-100 rounded-16 position-relative transition-2 h-100 d-flex flex-column">
                                        <Link
                                            to={`/product-details/${product.id}`}
                                            className="product-card__thumb flex-center"
                                        >
                                            <img 
                                                src={getImageUrl(product.image)} 
                                                alt={product.name}
                                                className="product-card__thumb-img"
                                                onError={(e) => {
                                                    e.target.src = 'assets/images/thumbs/product-img20.png';
                                                }}
                                            />
                                        </Link>
                                        <div className="product-card__content mt-12 d-flex flex-column flex-grow-1">
                                            <div className="flex-align gap-6">
                                                <span className="text-xs fw-bold text-gray-500">
                                                    {parseFloat(product.rating_average || 0).toFixed(1)}
                                                </span>
                                                <span className="text-15 fw-bold text-warning-600 d-flex">
                                                    <i className="ph-fill ph-star" />
                                                </span>
                                                <span className="text-xs fw-bold text-gray-500">
                                                    ({product.rating_count || 0})
                                                </span>
                                            </div>
                                            <h6 className="title text-lg fw-semibold mt-12 mb-8">
                                                <Link to={`/product-details/${product.id}`} className="link text-line-2">
                                                    {product.name}
                                                </Link>
                                            </h6>
                                            <div className="flex-align gap-4 d-none d-sm-flex">
                                                <span className="text-main-600 text-md d-flex">
                                                    <i className="ph-fill ph-storefront" />
                                                </span>
                                                <span className="text-gray-500 text-xs">
                                                    Groupe Délices Benkirane
                                                </span>
                                            </div>
                                            <div className="mt-auto pt-12">
                                                <ProductCardPriceQtyRow
                                                    className="mb-12"
                                                    price={(
                                                        <span className="text-heading text-md fw-semibold ">
                                                            {formatPrice(
                                                                isProductHasVariants(product) && getSelection(product).weightValue
                                                                    ? getLocationPrice(product) * getSelection(product).weightValue
                                                                    : getLocationPrice(product)
                                                            )} DH <span className="text-gray-500 fw-normal">/Qty</span>
                                                        </span>
                                                    )}
                                                    hint={weightHintId === product.id ? (
                                                        <span className="text-danger text-xs fw-medium d-block mt-4">
                                                            Choisissez un poids
                                                        </span>
                                                    ) : null}
                                                    quantityControl={(
                                                        <ProductCardQuantity
                                                            hasVariants={isProductHasVariants(product)}
                                                            selection={getSelection(product)}
                                                            onChange={(next) => updateSelection(product, next)}
                                                            collapsedUntilInteraction
                                                        />
                                                    )}
                                                />
                                                <button
                                                    onClick={(e) => handleAddToCart(e, product)}
                                                    disabled={addedProductId === product.id}
                                                    className={`product-card__cart btn ${
                                                        addedProductId === product.id 
                                                            ? 'btn-success' 
                                                            : 'new-arrival-btn'
                                                    } text-white hover-text-white py-11 px-24 rounded-pill flex-align gap-8 w-100 justify-content-center`}
                                                    style={{
                                                        transition: 'all 0.3s ease',
                                                        border: 'none',
                                                        cursor: addedProductId === product.id ? 'default' : 'pointer'
                                                    }}
                                                >
                                                    {addedProductId === product.id ? (
                                                        <>Ajouté <i className="ph ph-check-circle" /></>
                                                    ) : (
                                                        <>Ajouter au Panier <i className="ph ph-shopping-cart" /></>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </Slider>
                    </div>
                )}
            </div>
        </section>
    )
}

export default NewArrivalOne
